import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { BundleIcon, Paper, SearchField, Spinner, Table } from '@weareopenr/component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InfiniteData } from '@tanstack/react-query';
import { FieldValues, useForm } from 'react-hook-form';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { standaloneOptionGroupCols } from './Table';
import styles from './PriceSchemeOptionGroupOverrides.module.scss';
import EmptyState from '../EmptyState';
import { NoResultsFound } from '../NoResultsFound';
import { useMenus } from 'src/hooks/useMenus';
import { PriceSchemeFilters } from '../PriceSchemeFilters/PriceSchemeFilters';
import { useOptionGroups } from 'src/hooks/useOptionGroups/useOptionGroups';
import { usePriceSchemeFilters } from 'src/hooks/usePriceSchemeFilters';
import { OptionGroupsResponseBody } from 'src/api/getOptionGroups/types';
import { QueryKeys } from 'src/hooks/QueryKeys';
import {
  flattenOptionGroupsToModifiers,
  generateModifierFormDefaults,
  mapStandaloneOptionGroupToTableRow,
} from './utils';
import { BUNDLE_MODIFIER_FORM_ID } from './constants';
import { PricingSavePortal } from '../PricingSavePortal';
import { useSavePriceOverrides } from './useSavePriceOverrides';

const StandaloneOptionGroupOverrides = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    missingDefaultPriceFilter,
    setMissingDefaultPriceFilter,
    missingDefaultPrice,
    expandCollapseFilter,
    onExpandCollapseFilterChange,
    expanded,
    setExpanded,
  } = usePriceSchemeFilters();
  const debouncedSearchValue = useDebounce(searchValue);

  const { data: menus } = useMenus();

  const onNewOptionGroups = useCallback(({ pages }: InfiniteData<OptionGroupsResponseBody>) => {
    const optionGroups = pages?.flatMap(({ data }) => data) ?? [];
    const formValues = generateModifierFormDefaults(flattenOptionGroupsToModifiers(optionGroups));
    reset(formValues, { keepDirtyValues: true, keepErrors: true });
  }, []);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useOptionGroups({
    priceSchemeId,
    menuIds: searchMenus,
    name: debouncedSearchValue,
    missingDefaultPrice,
    options: {
      onSuccess: onNewOptionGroups,
    },
  });

  const optionGroups = useMemo(() => data?.pages?.flatMap(({ data }) => data) ?? [], [data]);
  const tableData = useMemo(() => optionGroups.map(mapStandaloneOptionGroupToTableRow), [optionGroups]);

  const defaultValues = useMemo(
    () => generateModifierFormDefaults(flattenOptionGroupsToModifiers(optionGroups)),
    [optionGroups],
  );

  const {
    data: missingDefaultPriceData,
    hasNextPage: hasNextMissingDefaultPricePage,
    fetchNextPage: fetchNextMissingDefaultPricePage,
    isFetchingNextPage: isFetchingNextMissingDefaultPricePage,
  } = useOptionGroups({
    priceSchemeId,
    menuIds: searchMenus,
    name: debouncedSearchValue,
    missingDefaultPrice: true,
  });

  useEffect(() => {
    if (hasNextMissingDefaultPricePage && !isFetchingNextMissingDefaultPricePage) {
      fetchNextMissingDefaultPricePage();
    }
  }, [hasNextMissingDefaultPricePage, isFetchingNextMissingDefaultPricePage, fetchNextMissingDefaultPricePage]);

  const missingDefaultPriceModifierIds = useMemo(
    () =>
      new Set(
        flattenOptionGroupsToModifiers(missingDefaultPriceData?.pages?.flatMap(({ data }) => data) ?? []).map(
          ({ modifier_id }) => modifier_id,
        ),
      ),
    [missingDefaultPriceData],
  );

  const { control, handleSubmit, reset, setValue } = useForm<FieldValues>({
    defaultValues,
    values: defaultValues,
  });

  const columns = useMemo(
    () => standaloneOptionGroupCols({ control, setValue, missingDefaultPriceModifierIds }),
    [control, setValue, missingDefaultPriceModifierIds],
  );

  const hasActiveFilters = !!debouncedSearchValue || searchMenus.length > 0 || missingDefaultPrice;

  const submitValues = useSavePriceOverrides({
    priceSchemeId,
    queryKey: [QueryKeys.optionGroups],
    control,
    reset,
  });

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  return (
    <div className={styles['container']}>
      <div className={styles['controls-row']}>
        <div className={styles['search-filter-container']}>
          <SearchField
            key="option-group-search"
            isDisabled={false}
            placeholder="Search"
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />
          <PriceSchemeFilters
            menus={menus}
            searchMenus={searchMenus}
            setSearchMenus={setSearchMenus}
            hasPricingCategoryFilterFeature={false}
            missingDefaultPriceFilter={missingDefaultPriceFilter}
            setMissingDefaultPriceFilter={setMissingDefaultPriceFilter}
            expandCollapseFilter={expandCollapseFilter}
            setExpandCollapseFilter={onExpandCollapseFilterChange}
          />
        </div>
        <PricingSavePortal control={control} reset={reset} form={BUNDLE_MODIFIER_FORM_ID} />
      </div>

      <form id={BUNDLE_MODIFIER_FORM_ID} onSubmit={handleSubmit(submitValues)} className={styles['rules-list']}>
        {tableData.length > 0 ? (
          <Table
            className={styles['table-fill-height']}
            data={tableData}
            columns={columns}
            columnHeadersToRightAlign={['override-price-cell']}
            getSubRows={(row) => row.modifiers}
            additionalConfig={{ state: { expanded }, onExpandedChange: setExpanded }}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : (
          <Paper className={styles['empty-state-paper']}>
            {hasActiveFilters ? (
              <NoResultsFound />
            ) : (
              <EmptyState emptySectionText="No Option Groups found" Icon={BundleIcon} />
            )}
          </Paper>
        )}
      </form>
    </div>
  );
};

export default StandaloneOptionGroupOverrides;
